import { useSettingBrandReviewPdpApiSplit } from "@faire/retailer-visitor-shared/settings/useSettingBrandReviewPdpApiSplit";
import { useLeanProductQuery } from "@faire/retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { useLocalNum } from "@faire/web--source/common/localization";
import { useMemo } from "react";

import { useBrandReviewSummary } from "../__internal__/hooks/useBrandReviewSummary";

export interface IBrandReviewSummary {
  overallRatingText: string | undefined;
  numberOfReviews: number | undefined;
}

export const useBrandReviewSummaryLean = (
  productToken: string
): IBrandReviewSummary => {
  const brandReviewPdpApiSplit = useSettingBrandReviewPdpApiSplit();
  const { localNum } = useLocalNum();

  const { brandReviewSummary } = useBrandReviewSummary(
    productToken,
    !brandReviewPdpApiSplit
  );

  const { data: resolvedProductResponse } = useLeanProductQuery(productToken, {
    enabled: brandReviewPdpApiSplit,
  });

  const overallRatingText: string | undefined = useMemo(() => {
    if (brandReviewPdpApiSplit) {
      return resolvedProductResponse?.brand?.average_rating;
    }

    if (!brandReviewSummary?.overallRating) {
      return undefined;
    }

    return localNum(brandReviewSummary?.overallRating ?? 0, {
      minimumSignificantDigits: 2,
      maximumSignificantDigits: 2,
    });
  }, [
    brandReviewSummary,
    localNum,
    resolvedProductResponse,
    brandReviewPdpApiSplit,
  ]);

  const numberOfReviews: number | undefined = useMemo(() => {
    if (brandReviewPdpApiSplit) {
      return resolvedProductResponse?.brand?.number_of_reviews;
    }

    return brandReviewSummary?.numberOfReviews;
  }, [brandReviewSummary, resolvedProductResponse, brandReviewPdpApiSplit]);

  return { overallRatingText, numberOfReviews };
};
